/**
 * Custom hook for running a callback at a fixed interval
 * Automatically pauses when tab is inactive
 */

import { useEffect, useRef } from 'react';

interface UseIntervalOptions {
  /**
   * Callback function to execute
   */
  callback: () => void;

  /**
   * Interval in milliseconds
   */
  interval: number;

  /**
   * Whether the interval is enabled (default: true)
   */
  enabled?: boolean;

  /**
   * Whether to pause when tab is inactive (default: true)
   */
  pauseWhenHidden?: boolean;

  /**
   * Whether to run immediately on mount (default: false)
   */
  runOnMount?: boolean;
}

export function useInterval({
  callback,
  interval,
  enabled = true,
  pauseWhenHidden = true,
  runOnMount = false,
}: UseIntervalOptions) {
  const savedCallback = useRef(callback);
  const intervalId = useRef<number | null>(null);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    if (!enabled) {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
      return;
    }

    const tick = () => {
      // Pause when tab is hidden (if enabled)
      if (pauseWhenHidden && document.hidden) {
        return;
      }

      savedCallback.current();
    };

    // Run immediately if requested
    if (runOnMount) {
      tick();
    }

    // Set up interval
    intervalId.current = setInterval(tick, interval);

    // Clean up on unmount or when dependencies change
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };
  }, [interval, enabled, pauseWhenHidden, runOnMount]);

  // Handle visibility change
  useEffect(() => {
    if (!pauseWhenHidden || !enabled) return;

    const handleVisibilityChange = () => {
      if (!document.hidden && intervalId.current === null) {
        // Tab became visible again, restart interval
        savedCallback.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pauseWhenHidden, enabled]);
}
